import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Copy, Loader2, ShieldCheck } from 'lucide-react';
import api from '../lib/api';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

/**
 * The page a creator lands on when they want to know what we hold about them, or want it gone.
 *
 * Two things shape the copy.
 *
 * It is written for someone who did not sign up for this and may be annoyed to learn the pool
 * exists. That means saying what is held before asking them for anything, using their words rather
 * than ours ("your public follower count", not "pooled metrics"), and never making them create an
 * account to exercise a right.
 *
 * And it describes what the system does rather than claiming a legal status. "We remove it" is a
 * fact we control; "PDPA compliant" is a judgement for counsel, and putting it on a public page
 * would be us marking our own homework.
 */

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'xhs', label: 'Xiaohongshu' },
];

type RequestResult = {
  status: 'PENDING_VERIFICATION' | 'NOT_FOUND' | 'ALREADY_REMOVED';
  message?: string;
  verificationCode?: string;
  placement?: string;
  handle?: string;
};

type VerifyResult = {
  status: 'APPLIED' | 'PENDING_VERIFICATION' | 'EXPIRED';
  message?: string;
  reason?: string;
};

function errorMessage(err: unknown, fallback: string): string {
  const data =
    err && typeof err === 'object' && 'response' in err
      ? (err as { response?: { data?: { error?: string; message?: string } } }).response?.data
      : null;
  return data?.error || data?.message || fallback;
}

const CreatorData: React.FC = () => {
  const [platform, setPlatform] = useState('instagram');
  const [handle, setHandle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState<RequestResult | null>(null);
  const [requestError, setRequestError] = useState('');

  const [verifying, setVerifying] = useState(false);
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  const [verifyError, setVerifyError] = useState('');
  const [copied, setCopied] = useState(false);

  const submitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!handle.trim()) {
      setRequestError('Enter the handle you want us to look up.');
      return;
    }
    setSubmitting(true);
    setRequestError('');
    setVerify(null);
    setVerifyError('');
    try {
      const { data } = await api.post('/public/creator-data-request', {
        platform,
        handle: handle.trim(),
      });
      setRequest(data as RequestResult);
    } catch (err: unknown) {
      setRequestError(errorMessage(err, 'Something went wrong. Please try again in a moment.'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitVerify = async () => {
    if (!request?.verificationCode) return;
    setVerifying(true);
    setVerifyError('');
    try {
      const { data } = await api.post('/public/creator-data-request/verify', {
        code: request.verificationCode,
      });
      setVerify(data as VerifyResult);
    } catch (err: unknown) {
      setVerifyError(errorMessage(err, 'We could not check that just now. Please try again shortly.'));
    } finally {
      setVerifying(false);
    }
  };

  const copyCode = async () => {
    if (!request?.verificationCode) return;
    try {
      await navigator.clipboard.writeText(request.verificationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is blocked in some browsers and embedded webviews. The code is on screen
      // and selectable, so this is a convenience failing, not the flow failing.
    }
  };

  const startOver = () => {
    setRequest(null);
    setVerify(null);
    setRequestError('');
    setVerifyError('');
  };

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 pb-8 pt-16">
        <div className="mx-auto max-w-3xl">
          <h1 className="mb-4 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent">
            Your data on kol.my
          </h1>
          <p className="text-lg leading-relaxed text-gray-400">
            If your public profile has been looked up by an agency using kol.my, a small amount of
            information about it may be stored here. This page tells you exactly what that is, and
            lets you have it removed.
          </p>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 pb-16 pt-4">
        <div className="mx-auto max-w-3xl space-y-12">
          <section>
            <h2 className="mb-4 text-2xl font-bold text-white">What may be stored</h2>
            <p className="mb-4 leading-relaxed text-gray-400">
              Only publicly available profile information — the same things anyone visiting your
              profile can see:
            </p>
            <ul className="list-disc space-y-2 pl-6 text-gray-400">
              <li>Your handle, display name and profile picture</li>
              <li>Your follower, following and post counts</li>
              <li>Average likes and an engagement rate calculated from those public numbers</li>
              <li>When each of those was last read, and from which platform</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-bold text-white">What is never stored</h2>
            <ul className="list-disc space-y-2 pl-6 text-gray-400">
              <li>Your email address, phone number or any other contact detail</li>
              <li>Anything from a private or followers-only account</li>
              <li>Direct messages, drafts, or anything behind a login</li>
              <li>Audience demographics or any analytics only you can see</li>
            </ul>
            <p className="mt-4 leading-relaxed text-gray-400">
              If you have connected your own Instagram account to an agency on kol.my, that is a
              separate thing you agreed to, and you can disconnect it from your profile page at any
              time.
            </p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-bold text-white">Have it removed</h2>
            <p className="mb-6 leading-relaxed text-gray-400">
              Tell us which account is yours. We will give you a short code to put in your bio so we
              can confirm the account is really yours — we have no other way to check, because we
              hold no email address for you. Once confirmed, everything above is deleted and your
              account cannot be added back by anyone browsing your profile.
            </p>

            {!request && (
              <form onSubmit={submitRequest} className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="flex flex-col gap-4 sm:flex-row">
                  <label className="sm:w-48">
                    <span className="mb-2 block text-sm font-medium text-gray-300">Platform</span>
                    <select
                      value={platform}
                      onChange={(e) => setPlatform(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-white focus:border-violet-500 focus:outline-none"
                    >
                      {PLATFORMS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex-1">
                    <span className="mb-2 block text-sm font-medium text-gray-300">Your handle</span>
                    <input
                      type="text"
                      value={handle}
                      onChange={(e) => setHandle(e.target.value)}
                      placeholder="@yourhandle"
                      autoComplete="off"
                      className="w-full rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-white placeholder-gray-600 focus:border-violet-500 focus:outline-none"
                    />
                  </label>
                </div>

                {requestError && (
                  <p className="flex items-start gap-2 text-sm text-red-400">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    {requestError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-6 py-3 font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Check this account
                </button>
                <p className="text-xs text-gray-500">
                  We do not ask for your email, and submitting this does not create an account.
                </p>
              </form>
            )}

            {request?.status === 'NOT_FOUND' && (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <p className="leading-relaxed text-gray-300">{request.message}</p>
                <button onClick={startOver} className="mt-4 text-sm text-violet-400 hover:text-violet-300">
                  Check another account
                </button>
              </div>
            )}

            {request?.status === 'ALREADY_REMOVED' && (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6">
                <ShieldCheck className="mb-3 h-6 w-6 text-emerald-400" />
                <p className="leading-relaxed text-gray-200">{request.message}</p>
                <button onClick={startOver} className="mt-4 text-sm text-violet-400 hover:text-violet-300">
                  Check another account
                </button>
              </div>
            )}

            {request?.status === 'PENDING_VERIFICATION' && verify?.status !== 'APPLIED' && (
              <div className="space-y-5 rounded-2xl border border-white/10 bg-white/5 p-6">
                <div>
                  <p className="mb-1 text-sm text-gray-400">
                    We do hold public profile information for{' '}
                    <span className="font-medium text-white">{request.handle}</span>.
                  </p>
                  <p className="leading-relaxed text-gray-300">
                    Add this code to {request.placement}, then come back and confirm. You can delete
                    it again straight afterwards.
                  </p>
                </div>

                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-zinc-900 px-4 py-3">
                  <code className="flex-1 select-all font-mono text-lg tracking-wide text-violet-300">
                    {request.verificationCode}
                  </code>
                  <button
                    onClick={copyCode}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-sm text-gray-300 hover:bg-white/5"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>

                {verify?.status === 'PENDING_VERIFICATION' && (
                  <p className="flex items-start gap-2 text-sm text-amber-300">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    {verify.message}
                  </p>
                )}
                {verify?.status === 'EXPIRED' && (
                  <p className="text-sm text-amber-300">{verify.message}</p>
                )}
                {verifyError && (
                  <p className="flex items-start gap-2 text-sm text-red-400">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    {verifyError}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={submitVerify}
                    disabled={verifying}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-6 py-3 font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {verifying && <Loader2 className="h-4 w-4 animate-spin" />}
                    I have added the code
                  </button>
                  <button onClick={startOver} className="text-sm text-gray-400 hover:text-gray-300">
                    Start over
                  </button>
                </div>

                <p className="text-xs leading-relaxed text-gray-500">
                  Cannot edit your bio, or the check keeps failing? Email{' '}
                  <a href="mailto:i@kol.my" className="text-violet-400 hover:text-violet-300">
                    i@kol.my
                  </a>{' '}
                  and a person will handle it. Nothing here is decided only by a machine.
                </p>
              </div>
            )}

            {verify?.status === 'APPLIED' && (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6">
                <CheckCircle2 className="mb-3 h-6 w-6 text-emerald-400" />
                <p className="leading-relaxed text-gray-200">{verify.message}</p>
                <button onClick={startOver} className="mt-4 text-sm text-violet-400 hover:text-violet-300">
                  Remove another account
                </button>
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-bold text-white">Questions</h2>
            <p className="leading-relaxed text-gray-400">
              Email{' '}
              <a href="mailto:i@kol.my" className="text-violet-400 hover:text-violet-300">
                i@kol.my
              </a>{' '}
              and a person will answer. Our full{' '}
              <Link to="/privacy" className="text-violet-400 hover:text-violet-300">
                privacy policy
              </Link>{' '}
              covers everything else we hold.
            </p>
          </section>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default CreatorData;
